import { Injectable } from '@angular/core';
import { ActiveState, EntityState, StoreConfig } from '@datorama/akita';
import { Y42EntityStore } from '@y42/state';
import { Observable, Subject } from 'rxjs';
import type { GitFileStatusRow } from '../worker/actions/git-status-short';
import type { GitBranch, GitFSStatus, GitRepo, GitTreeItem, StatusRow } from '../worker/models/git-worker.model';
import type { GitEvent } from './git-event.model';

export interface GitState extends EntityState<GitBranch, GitBranch['name']>, ActiveState<GitBranch['name']> {
  ui: {
    activeTreeItem?: GitTreeItem;
    numberOfConflictedFiles: number;
    loading: number;
    status: GitFSStatus | null;
    repo: GitRepo | null;
    repoInitializedFor: string | null;

    /**
     * Number of commits ahead of origin
     */
    ahead: number;
    /**
     * Number of commits behind origin
     */
    behind: number;

    /** The SHA of the last commit **on origin** for the active branch. */
    currentOriginCommitSha: string | null;
    /** The SHA of the last **local** commit for the active branch */
    currentLocalCommitSha: string | null;
    /**
     * Output of `git status --short`
     */
    fileStatuses: Record<string, StatusRow>;
  };
  default: GitBranch['name'] | null;
}

const initialState: GitState = {
  ui: {
    numberOfConflictedFiles: 0,
    loading: 0,
    status: null,
    repo: null,
    repoInitializedFor: null,
    ahead: 0,
    behind: 0,
    currentOriginCommitSha: null,
    currentLocalCommitSha: null,
    fileStatuses: {},
  },
  active: null,
  default: null,
};

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'git', idKey: 'name' })
export class GitStore extends Y42EntityStore<GitState> {
  constructor() {
    super(initialState);
  }

  private readonly event$$ = new Subject<GitEvent>();
  readonly event$: Observable<GitEvent> = this.event$$;

  override setLoading(loading = true) {
    const currentLoading = this.getValue().ui.loading || 0;
    return this.updateUI({
      loading: loading ? currentLoading + 1 : currentLoading - 1,
    });
  }

  updateUI(ui: Partial<GitState['ui']>) {
    return this.update((st) => ({
      ui: {
        ...st.ui,
        ...ui,
      },
    }));
  }

  setStatus(status: GitFSStatus | GitFSStatus['status'] | null) {
    if (status) {
      this.setError(null);
    }

    if (typeof status === 'string') {
      return this.updateUI({ status: { status } });
    }

    return this.updateUI({ status });
  }

  setFileStatuses(fileStatusRows: GitFileStatusRow[]) {
    const fileStatuses = fileStatusRows.reduce((all, current) => {
      const [file, ...statusRow] = current;
      all[file] = statusRow;
      return all;
    }, {} as Record<string, StatusRow>);
    this.updateUI({ fileStatuses });
  }

  setGitRepo(repo: GitRepo) {
    this.updateUI({ repo });
  }

  setRepoInitialized(name: string | null) {
    this.updateUI({ repoInitializedFor: name });
  }

  setDefault(branchName: string) {
    this.update({ default: branchName });
  }

  setCurrentOriginCommitSha(currentOriginCommitSha: string | null) {
    this.updateUI({ currentOriginCommitSha: currentOriginCommitSha });
  }

  setCurrentLocalCommitSha(currentLocalCommitSha: string) {
    this.updateUI({ currentLocalCommitSha });
  }

  emit<Event extends GitEvent>(type: Event['type'], params: Event['params']) {
    this.event$$.next({ type, params });
  }
}
